import {useCallback, useEffect, useRef, useState} from 'react';

function countWordsFromMarkdown(markdown: string): number {
    const text = markdown
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`[^`\n]+`/g, '')
        .replace(/!\[.*?\]\(.*?\)/g, '')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/(\*\*|__)(.*?)\1/gs, '$2')
        .replace(/([*_])(.*?)\1/gs, '$2')
        .replace(/~~(.*?)~~/gs, '$1')
        .replace(/^\s*[-*+]\s+/gm, '')
        .replace(/^\s*\d+\.\s+/gm, '')
        .replace(/^\s*>\s*/gm, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!text) return 0;
    return text.split(' ').filter(Boolean).length;
}

interface EditorProps {
    initialContent?: string;
    onChange?: (content: string) => void;
    placeholder?: string;
    fontSize?: number;
    onImageQueued?: (blobUrl: string, file: File) => void;
    onLocationClick?: () => void;
}

export function Editor({
                           initialContent,
                           onChange,
                           placeholder = 'Start writing...',
                           fontSize = 16,
                           onImageQueued,
                           onLocationClick,
                       }: EditorProps) {
    const [content, setContent] = useState(initialContent || '');
    const [wordCount, setWordCount] = useState(() => countWordsFromMarkdown(initialContent || ''));
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setContent(value);
        setWordCount(countWordsFromMarkdown(value));
        onChange?.(value);
    }, [onChange]);

    const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        e.target.value = '';
        const blobUrl = URL.createObjectURL(file);
        onImageQueued?.(blobUrl, file);
        const imageMarkdown = `![${file.name}](${blobUrl})`;
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const newValue = content.slice(0, start) + imageMarkdown + content.slice(end);
        setContent(newValue);
        setWordCount(countWordsFromMarkdown(newValue));
        onChange?.(newValue);
        requestAnimationFrame(() => {
            el.selectionStart = el.selectionEnd = start + imageMarkdown.length;
            el.focus();
        });
    }, [content, onChange, onImageQueued]);

    return (
        <div className="editor-container relative rounded border border-paper-300 bg-paper-100">
            <textarea
                ref={textareaRef}
                value={content}
                onChange={handleChange}
                placeholder={placeholder}
                className="w-full min-h-[300px] p-4 outline-none bg-paper-100 resize-none font-[inherit] text-ink"
                style={{fontSize: `${fontSize}px`}}
            />
            <div className="flex items-center gap-0.5 p-2 border-b border-t border-paper-300">
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Insert Image"
                    className="p-1.5 rounded-sm transition-colors text-ink-500 hover:bg-paper-200 hover:text-ink"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                    className="hidden"
                    onChange={handleImageUpload}
                />
                <button
                    type="button"
                    onClick={() => onLocationClick?.()}
                    title="Set Location"
                    className="p-1.5 rounded-sm transition-colors text-ink-500 hover:bg-paper-200 hover:text-ink"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                </button>
                <span className="ml-auto text-xs text-ink-400 pr-1 select-none">{wordCount} words</span>
            </div>

        </div>
    );
}
